// Record pages: pop-outs live in the URL — #/deals/<id>?panel=vault&folder=room&note=<id>.
// One writer (_write), one pushed history entry per pop-out session, Back closes it.
// The pure rules are in record_panels_core.jsx; docs/record-page-redesign.md "Popout convention".
//
// One IIFE. Later steps (4b overlays, 8 research) add their code inside it, above
// the closing anchor comment, and reuse these private names rather than
// redeclaring them: core, EMPTY, VIAS, viaOf, getHash, subscribe, stateNow,
// _write, lastReason, noteHosts.
(function () {
  const core = window.RecordPanelsCore;
  const EMPTY = Object.freeze({});
  const VIAS = ['button', 'escape', 'link', 'back'];
  const viaOf = (o) => (o && VIAS.includes(o.via) ? o.via : 'button');

  // ── the URL store (a string snapshot: equal values keep identity)
  const getHash = () => (typeof window === 'undefined' ? '' : (window.location.hash || ''));
  const subscribe = (cb) => {
    window.addEventListener('hashchange', cb);
    window.addEventListener('popstate', cb);
    window.addEventListener(core.EVT_PARAMS, cb);
    return () => {
      window.removeEventListener('hashchange', cb);
      window.removeEventListener('popstate', cb);
      window.removeEventListener(core.EVT_PARAMS, cb);
    };
  };
  const stateNow = () => { try { return window.history.state; } catch (_) { return null; } };

  // ── the one writer (pushState/replaceState fire no event, so it announces the change)
  const _write = (method, state, hash, detail, quiet) => {
    try {
      if (method === 'push') window.history.pushState(state, '', hash);
      else window.history.replaceState(state, '', hash);
    } catch (_) {
      try { window.location.hash = hash; } catch (__) { /* nothing left to try */ }
      return;                                    // the assignment fires hashchange itself
    }
    if (quiet) return;
    try { window.dispatchEvent(new CustomEvent(core.EVT_PARAMS, { detail: Object.assign({}, detail, { method }) })); } catch (_) {}
  };

  // Why the last panel change happened: { rec, from, to, via }. PanelHost's beacon (4b) consumes it.
  let lastReason = null;

  // ── Back, once: refuse writes until the popstate (800 ms fallback; strips if Back went nowhere)
  let backFrom = null; let backTimer = null;
  const clearBack = () => { if (backTimer) clearTimeout(backTimer); backTimer = null; backFrom = null; };
  const _goBack = (fallbackHash, detail) => {
    backFrom = getHash();
    // One task later, so a navigation made in the same click (NoteModal: go to the
    // company, then close) commits its hash first; then there is nothing to go back from.
    backTimer = setTimeout(() => {
      if (getHash() !== backFrom) { clearBack(); lastReason = null; return; }   // navigated away: the pop-out entry stays in history
      backTimer = setTimeout(() => {
        const stuck = backFrom != null && getHash() === backFrom;
        clearBack();
        if (stuck) _write('replace', stateNow(), fallbackHash, detail);
      }, 800);
      try { window.history.back(); } catch (_) { clearBack(); _write('replace', stateNow(), fallbackHash, detail); }
    }, 0);
  };
  window.recordBackPending = () => backFrom != null;

  // ── legacy links → their pop-out (core.ALIASES; inert in 4a: no panel is available yet)
  const _rewriteAliases = (quiet) => {
    const hash = getHash();
    const parsed = core.parseRecordHash(hash);
    if (!parsed) return false;
    const surface = window.recordSurface();
    const out = core.applyAliases(parsed.kind, parsed.params,
      { surface, available: (name) => window.recordPanelAvailable(name, surface) });
    if (!out.changed) return false;
    _write('replace', stateNow(), core.hashWithParams(hash, out.params),
      { kind: parsed.kind, id: parsed.id, params: out.params, via: 'link' }, quiet);
    return true;
  };
  // Registered at module load, before App's own hashchange listener.
  // Our own Back's popstate keeps the reason (a button/escape close); anyone else's drops it.
  window.addEventListener('popstate', () => { if (backFrom != null) clearBack(); else lastReason = null; });
  window.addEventListener('hashchange', () => { _rewriteAliases(false); });
  window.addEventListener('popstate', () => { _rewriteAliases(false); });

  window.setRecordParams = (rec, patch, opts) => {
    if (!rec || !rec.kind || rec.id == null || rec.id === '') return false;
    if (backFrom != null) return false;                        // a Back is still landing
    const hash = getHash();
    const parsed = core.parseRecordHash(hash);
    if (!parsed || !core.sameRecord(parsed, rec)) return false;
    const cur = parsed.params;
    const next = core.mergeParams(cur, patch || {});
    const plan = core.planWrite(cur, next, stateNow(), core.recKey(rec));
    if (plan.method === 'none') return true;
    const via = viaOf(opts);
    if ((cur.panel || null) !== (next.panel || null)) {        // a note, folder or tab write keeps the last panel's reason
      lastReason = { rec: { kind: parsed.kind, id: parsed.id }, from: cur.panel || null, to: next.panel || null, via };
    }
    const nextHash = core.hashWithParams(hash, next);
    const detail = { kind: parsed.kind, id: parsed.id, params: next, via };
    if (plan.method === 'back') { _goBack(nextHash, detail); return true; }
    _write(plan.method, plan.state, nextHash, detail);
    return true;
  };

  window.usePanelParam = (rec) => {
    const kind = (rec && rec.kind) || null;
    const id = rec && rec.id != null && rec.id !== '' ? String(rec.id) : null;
    React.useState(() => { _rewriteAliases(true); return 0; });   // before the first render; quiet: no event mid-render
    const hash = React.useSyncExternalStore(subscribe, getHash, getHash);
    const params = React.useMemo(() => {
      if (!kind || id == null) return EMPTY;
      const parsed = core.parseRecordHash(hash);
      return parsed && core.sameRecord(parsed, { kind, id }) ? Object.freeze(parsed.params) : EMPTY;
    }, [hash, kind, id]);
    const r = React.useMemo(() => ({ kind, id }), [kind, id]);
    const open = React.useCallback((name, p, o) => (
      !!kind && id != null && !!core.panelMeta(name) && window.setRecordParams(r, core.openPatch(name, p || {}), { via: viaOf(o) })
    ), [r]);   // eslint-disable-line react-hooks/exhaustive-deps
    const set = React.useCallback((patch, o) => {
      if (!kind || id == null) return false;
      const parsed = core.parseRecordHash(getHash());
      if (!parsed || !core.sameRecord(parsed, r) || !parsed.params.panel) return false;
      const clean = {};
      Object.keys(patch || {}).forEach((k) => { if (k !== 'panel' && core.PANEL_KEYS.includes(k)) clean[k] = patch[k]; });
      return window.setRecordParams(r, clean, { via: viaOf(o) });
    }, [r]);   // eslint-disable-line react-hooks/exhaustive-deps
    const close = React.useCallback((o) => {
      if (!kind || id == null) return false;
      const patch = {}; core.PANEL_KEYS.forEach((k) => { patch[k] = null; });
      return window.setRecordParams(r, patch, { via: viaOf(o) });
    }, [r]);   // eslint-disable-line react-hooks/exhaustive-deps
    const openNote = React.useCallback((noteId, o) => (
      !!kind && id != null && noteId != null && noteId !== '' && window.setRecordParams(r, { note: String(noteId) }, { via: viaOf(o) })
    ), [r]);   // eslint-disable-line react-hooks/exhaustive-deps
    const closeNote = React.useCallback((o) => (
      !!kind && id != null && window.setRecordParams(r, { note: null }, { via: viaOf(o) })
    ), [r]);   // eslint-disable-line react-hooks/exhaustive-deps
    const name = params.panel || null;
    const meta = core.panelMeta(name);
    return { name, params, isWorkspace: !!meta && meta.form === 'workspace', open, set, close,
             note: params.note || null, openNote, closeNote };
  };

  // ── one note reader per record page, outside every Section and workspace
  const noteHosts = new Map();   // recKey → mounted count (StrictMode mounts twice)
  window.NoteHost = ({ kind, id, onChange }) => {
    const p = window.usePanelParam({ kind, id });
    const key = core.recKey({ kind, id });
    React.useEffect(() => {
      noteHosts.set(key, (noteHosts.get(key) || 0) + 1);
      return () => { const n = (noteHosts.get(key) || 1) - 1; if (n > 0) noteHosts.set(key, n); else noteHosts.delete(key); };
    }, [key]);
    const NoteModal = window.NoteModal;
    if (!p.note || !NoteModal) return null;
    return ReactDOM.createPortal(
      <NoteModal noteId={p.note} onClose={() => p.closeNote({ via: 'button' })} onChange={onChange} />,
      document.body,
    );
  };

  // True when the record on screen owns notes (its NoteHost is mounted): the note
  // opens as ?note=. False off record pages (CIM, Calling, Signals) — open locally.
  window.openRecordNote = (noteId, opts) => {
    if (noteId == null || noteId === '') return false;
    const parsed = core.parseRecordHash(getHash());
    if (!parsed || !noteHosts.has(core.recKey(parsed))) return false;
    window.setRecordParams({ kind: parsed.kind, id: parsed.id }, { note: String(noteId) }, { via: viaOf(opts) });
    return true;
  };

  // ── 4b · layout flag: the team switch (2026-10-22) ────────────────────────
  // whoami.record_layout is the team's layout (Settings → Feature flags, admin; the
  // app_settings row 'record_layout'). An admin may keep a personal preview
  // (⌘K "Preview record layout: new / classic" → atlas.recordPreview, written
  // only once whoami says admin; "Use team layout" clears it). A non-admin's is
  // dropped once whoami says so, and so is an admin's when they save the team's
  // layout in Settings. The pre-switch ⌘K value (atlas.recordV2, anyone's) is
  // never read and is dropped on load. Before whoami answers (or when it fails)
  // the last team layout this browser saw stands in (atlas.recordTeamLayout),
  // else RECORD_V2_DEFAULT. core.resolveRecordV2 is the rule. The value is read
  // once per page load, then latched: it moves only in this tab — whoami
  // answering, an admin's preview, an admin's Settings change — and each move
  // dispatches 'atlas-layout' so App re-keys the record page instead of
  // changing its hook set (React #310; a phone's key stays 'phone'). Another
  // tab's change waits for this tab's next load. The in-memory copy keeps it
  // working when storage throws.
  const _layout = { v2: null, team: null, admin: null, preview: null, remembered: null };
  const _store = (fn) => { try { fn(window.localStorage); } catch (_) { /* memory only */ } };
  const _resolveV2 = () => core.resolveRecordV2({
    team: _layout.team, admin: _layout.admin, preview: _layout.preview, remembered: _layout.remembered,
    dflt: core.RECORD_V2_DEFAULT,
  });
  const _bootLayout = () => {
    if (_layout.v2 != null) return;
    Object.assign(_layout, core.readRecordPrefs((k) => window.localStorage.getItem(k)));
    _store((s) => s.removeItem(core.RECORD_V2_KEY));
    _layout.v2 = _resolveV2();
  };
  const _dropPreview = () => {
    _layout.preview = null;
    _store((s) => s.removeItem(core.RECORD_PREVIEW_KEY));
  };
  const _relatch = () => {
    const next = _resolveV2();
    if (next === _layout.v2) return;
    _layout.v2 = next;
    window.dispatchEvent(new CustomEvent(core.EVT_LAYOUT, { detail: { layout: next ? 'v2' : 'classic' } }));
  };
  window.recordV2 = () => { _bootLayout(); return _layout.v2; };
  // whoami answered (app.jsx, Settings), or an admin saved the team's layout
  // (Settings: { dropPreview: true }, so the admin sees what the team sees).
  window.setRecordTeamLayout = (layout, opts) => {
    _bootLayout();
    const o = opts || {};
    if (typeof o.admin === 'boolean') _layout.admin = o.admin;
    if (core.RECORD_LAYOUTS.includes(layout)) {
      _layout.team = layout;
      _layout.remembered = layout;
      _store((s) => s.setItem(core.RECORD_TEAM_KEY, layout));
    }
    if (_layout.preview != null && (_layout.admin === false || o.dropPreview === true)) _dropPreview();
    _relatch();
  };
  // An admin's own preview (⌘K): true / false pins this browser, null goes back
  // to the team's layout. A pin is refused (false) until whoami says admin.
  window.setRecordV2 = (on) => {
    _bootLayout();
    if (on == null) _dropPreview();
    else if (_layout.admin !== true) return false;
    else {
      _layout.preview = on ? 'v2' : 'classic';
      _store((s) => s.setItem(core.RECORD_PREVIEW_KEY, _layout.preview));
    }
    _relatch();
    return true;
  };
  // What ⌘K and Settings read: the team's layout (null until whoami answers),
  // whether whoami says admin, this browser's preview, and the layout in force.
  window.recordLayoutState = () => {
    _bootLayout();
    return {
      team: _layout.team, admin: _layout.admin, preview: _layout.preview,
      layout: _layout.v2 ? 'v2' : 'classic',
    };
  };
  window.recordSurface = () => (window.AtlasDevice && window.AtlasDevice.phone
    ? 'phone' : (window.recordV2() ? 'v2' : 'classic'));
  // What the page rendered, for the record-view beacons: the v2 RecordPage, else
  // 'classic' (the classic desktop page, or a phone page; `device` says which).
  window.recordLayout = () => (window.recordSurface() === 'v2' ? 'v2' : 'classic');

  // ── write gate (window.AtlasWhoami, set by app.jsx from whoami; the
  // session module views/login.jsx owns the AtlasAuth name)
  window.recordCanWrite = () => {
    const a = window.AtlasWhoami || {};
    if (window.AtlasIsGuest || !a.hasActiveUser) return false;
    if (a.canEditRecords != null) return !!a.canEditRecords;
    return ['admin', 'editor'].includes(String(a.role || '').toLowerCase());
  };
  window.recordAuthKnown = () => !!(window.AtlasWhoami && window.AtlasWhoami.known);

  // ── wave D: the reference drawers shared by every kind. A kind's body is a
  // window component its own page step registers; a kind whose body hasn't
  // shipped strips the panel (applies → false).
  const DETAILS_BODY = Object.freeze({ deal: 'DealDetailsBody', company: 'CompanyDetailsBody', contact: 'ContactDetailsBody' });
  const HISTORY_TABS = Object.freeze({ deal: ['changes', 'stages', 'sync'], company: ['changes', 'buyer', 'sync'], contact: ['changes', 'sync'] });
  window.RECORD_HISTORY_TABS = HISTORY_TABS;   // the one copy; activity.jsx's RecordHistoryBody reads it at render
  const bodyFor = (names, kind) => { const B = window[names[kind]]; return typeof B === 'function' ? B : null; };

  // ── the registry: name → PANEL_META[name] + where it renders per surface.
  // 'host' = PanelHost renders entry.render(api); 'section' = the page's own
  // section renders it and PanelHost only gates + beacons; missing/null =
  // stripped on that surface.
  const RECORD_PANELS = {
    // The Buyers workspace (step 5): the deal's board and outreach tracker on
    // one state; v2 desktop/iPad only. Body: views/buyers_workspace.jsx.
    buyers: {
      ...core.PANEL_META.buyers,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts) => !facts.shell,
      valid: (p) => !p.tab || p.tab === 'board' || p.tab === 'outreach',
      render: (api) => (window.BuyersWorkspace ? <window.BuyersWorkspace api={api} /> : null),
    },
    // ── wave D (step 10): All details, People, History, Meetings & calls, Files.
    details: {
      ...core.PANEL_META.details,
      host: { classic: null, v2: 'host', phone: 'host' },
      applies: (facts, kind) => !!bodyFor(DETAILS_BODY, kind),
      render: (api) => { const B = bodyFor(DETAILS_BODY, api.rec.kind); return B ? <B api={api} /> : null; },
    },
    people: {
      ...core.PANEL_META.people,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts, kind) => (kind === 'contact' ? false : kind === 'deal' ? !!facts.companyId : true),
      render: (api) => (window.RecordPeopleBody ? <window.RecordPeopleBody api={api} /> : null),
    },
    history: {
      ...core.PANEL_META.history,
      host: { classic: null, v2: 'host', phone: null },
      applies: () => true,
      valid: (p, kind) => !p.tab || (HISTORY_TABS[kind] || []).includes(p.tab),
      render: (api) => (window.RecordHistoryBody ? <window.RecordHistoryBody api={api} /> : null),
    },
    calls: {
      ...core.PANEL_META.calls,
      host: { classic: null, v2: 'host', phone: null },
      applies: () => true,
      valid: (p) => !p.call || /^\S{1,64}$/.test(String(p.call)),
      render: (api) => (window.RecordCallsBody ? <window.RecordCallsBody api={api} /> : null),
    },
    files: {
      ...core.PANEL_META.files,
      host: { classic: null, v2: 'host', phone: 'host' },
      // Deals: shells, guests and a seller deal with no company (no vault to hold its attachments).
      applies: (facts, kind) => (kind === 'deal' ? (!!facts.shell || !!window.AtlasIsGuest || !facts.companyId) : (facts.known ? !facts.sellerCompany : null)),
      valid: (p) => !p.status || ['available', 'missing', 'all'].includes(p.status),
      render: (api) => (window.RecordFilesWorkspace ? <window.RecordFilesWorkspace api={api} /> : null),
    },
    // ── wave D (step 11): Ownership & acquisition, Marketing materials, MyExit.
    ownership: {
      ...core.PANEL_META.ownership,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts, kind) => (kind === 'deal' ? (!facts.shell && !!facts.companyId) : (facts.known ? !!facts.sellerCompany : null)),
      render: (api) => (window.OwnershipDrawerBody ? <window.OwnershipDrawerBody api={api} /> : null),
    },
    marketing: {
      ...core.PANEL_META.marketing,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts) => (facts.known ? !!facts.sellerCompany : null),
      render: (api) => (window.MarketingDrawerBody ? <window.MarketingDrawerBody api={api} /> : null),
    },
    myexit: {
      ...core.PANEL_META.myexit,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts) => (facts.known ? !!facts.sellerCompany : null),
      render: (api) => (window.MyExitDrawerBody ? <window.MyExitDrawerBody api={api} /> : null),
    },
    // ── wave D (step 12): the Firm workspace (roster buyer firms). Body: views/buyer_firm_v2.jsx.
    firm: {
      ...core.PANEL_META.firm,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts) => (facts.known ? !!facts.buyerFirm : null),
      valid: (p) => !p.tab || ['deals', 'passes', 'team', 'portfolio', 'acquisitions', 'activity', 'site'].includes(p.tab),
      render: (api) => (window.FirmWorkspace ? <window.FirmWorkspace api={api} /> : null),
    },
    // CompanyVaultSection (4a) renders the vault from ?panel=vault; applies()
    // mirrors exactly where it is mounted (QW1 / isSellerCompany). ?file= opens
    // that file's preview; the section drops a malformed one and keeps the folder.
    vault: {
      ...core.PANEL_META.vault,
      host: { classic: 'section', v2: 'section', phone: 'section' },
      applies: (facts, kind) => (kind === 'deal'
        ? (!facts.shell && !!facts.companyId)
        : (facts.known ? !!facts.sellerCompany : null)),
    },
    // The research run drawer (views/research.jsx, step 8): any finding,
    // verdict or fill with its reasoning, sources and steps — on every surface
    // (on a phone the drawer is the full-screen sheet). Guests: PANEL_META's
    // guest:false strips it; valid() is the src/run/field allow-list.
    research: {
      ...core.PANEL_META.research,
      host: { classic: 'host', v2: 'host', phone: 'host' },
      applies: () => true,
      valid: (p, kind) => core.validResearchParams(kind, p),
      title: (api) => (window.ResearchRunTitle ? <window.ResearchRunTitle api={api} /> : 'Research'),
      render: (api) => (window.ResearchRunBody ? <window.ResearchRunBody api={api} /> : null),
    },
    // Market workspace + Company intel drawer (step 6): company-scoped on
    // deals and people; v2 desktop/iPad only. Bodies: comps.jsx, company_intel.jsx.
    market: {
      ...core.PANEL_META.market,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts, kind) => (kind === 'deal' ? (!facts.shell && !!facts.companyId)
        : kind === 'company' ? (facts.known ? !!facts.sellerCompany : null)
        : (!facts.companyId ? false : facts.companySeller == null ? null : !!facts.companySeller)),
      valid: (p) => !p.tab || ['market', 'nearby', 'ours', 'acquirers'].includes(p.tab),
      render: (api) => (window.MarketWorkspace ? <window.MarketWorkspace api={api} /> : null),
    },
    intel: {
      ...core.PANEL_META.intel,
      host: { classic: null, v2: 'host', phone: null },
      applies: (facts, kind) => RECORD_PANELS.market.applies(facts, kind),
      valid: (p) => !p.focus || ['brief', 'facts', 'firmographics'].includes(p.focus),
      render: (api) => (window.CompanyIntelBody ? <window.CompanyIntelBody api={api} /> : null),
    },
    // ── wave D (step 13): Programs, Outreach history (people).
    programs: {
      ...core.PANEL_META.programs,
      host: { classic: null, v2: 'host', phone: null },
      applies: () => true,
      valid: (p) => !p.tab || ['referral', 'made', 'events', 'gpp', 'brand'].includes(p.tab),
      render: (api) => (window.ContactProgramsBody ? <window.ContactProgramsBody api={api} /> : null),
    },
    outreach: {
      ...core.PANEL_META.outreach,
      host: { classic: null, v2: 'host', phone: null },
      applies: () => true,
      render: (api) => (window.ContactOutreachBody ? <window.ContactOutreachBody api={api} /> : null),
    },
  };
  window.RECORD_PANELS = RECORD_PANELS;
  // Own-property lookup, so ?panel=constructor / __proto__ stays inert (URL input).
  // Not frozen: tests and browser checks add fakes.
  const panelEntry = (name) => (typeof name === 'string'
    && Object.prototype.hasOwnProperty.call(RECORD_PANELS, name) ? RECORD_PANELS[name] : null);
  // An entry callback that throws counts as `dflt` (never takes the page down).
  const _call = (fn, dflt) => { try { return fn(); } catch (_) { return dflt; } };

  window.recordPanelAvailable = (name, surface) => {
    const e = panelEntry(name);
    return !!(e && e.host && e.host[surface]);
  };
  window.recordOutreachAsPanel = () =>
    window.recordSurface() === 'v2' && window.recordPanelAvailable('buyers', 'v2');

  // A pop-out by name on the record on screen (step 8's openers). False, and
  // nothing written, when the kind, guest, host, valid or write gate would
  // strip it (a write panel also while whoami is unknown); the caller then
  // falls back to today's UI. `applies` is not checked here: it needs the
  // page's facts, so PanelHost strips that case after the write.
  window.openRecordPanel = (name, params, opts) => {
    const parsed = core.parseRecordHash(getHash());
    const meta = panelEntry(name) || core.panelMeta(name);   // same meta as the verdict
    if (!parsed || !meta || !(meta.kinds || []).includes(parsed.kind)) return false;
    if (window.AtlasIsGuest && !meta.guest) return false;
    if (meta.write && !(window.recordAuthKnown() && window.recordCanWrite())) return false;
    if (!window.recordPanelAvailable(name, window.recordSurface())) return false;
    const e = panelEntry(name);
    if (e && typeof e.valid === 'function'
      && !_call(() => !!e.valid(core.cleanPanelParams(name, params || {}), parsed.kind), false)) return false;
    return window.setRecordParams({ kind: parsed.kind, id: parsed.id },
      core.openPatch(name, params || {}), { via: viaOf(opts) });
  };

  // ── the gate: pure over the registry, core.gatePanel and the write gate.
  const recordPanelVerdict = ({ name, params, kind, surface, guest, facts }) => {
    if (!name) return 'ok';
    const entry = panelEntry(name);
    const meta = entry || core.panelMeta(name);
    const host = (entry && entry.host && entry.host[surface]) || null;
    // A broken applies/valid strips; it never hangs on 'wait'.
    const applies = entry && typeof entry.applies === 'function'
      ? _call(() => { const a = entry.applies(facts || {}, kind); return a == null ? null : !!a; }, false)
      : null;
    const valid = entry && typeof entry.valid === 'function'
      ? _call(() => !!entry.valid(params || {}, kind), false)
      : undefined;
    return core.gatePanel({
      name, params: params || {}, kind, surface, guest: !!guest,
      canWrite: window.recordAuthKnown() ? window.recordCanWrite() : null,
      applies, meta, host, valid,
    });
  };
  window.recordPanelVerdict = recordPanelVerdict;

  // ── panel beacons (api.jsx recordView; payload = ui_events.py RecordView)
  const _panelBeacon = (kind, variant, panel, action, via) => {
    if (window.AtlasIsGuest || !window.AtlasApi || !window.AtlasApi.recordView) return;
    window.AtlasApi.recordView({
      page: kind,
      variant: variant,
      device: (window.AtlasDevice && window.AtlasDevice.kind) || 'desktop',
      layout: window.recordLayout(),
      panel: panel,
      action: action,
      via: via,
    });
  };
  // Consume the pending reason (lastReason) only when it describes exactly this
  // transition, so a stale one (a strip, a cancelled Back) never labels a later one.
  const _takeReason = (rec, was, now) => {
    const r = lastReason;
    if (!r || !rec || !core.sameRecord(r.rec, rec)) return null;
    if ((r.from || null) !== (was || null) || (r.to || null) !== (now || null)) return null;
    lastReason = null;
    return r;
  };
  window.takeRecordParamsReason = _takeReason;
  const _VARIANT_FALLBACK = { deal: 'seller', company: 'company', contact: 'person' };

  // ── workspaces: VaultManager's shell, lifted. No transform or animation: a
  // transform would make it the containing block of the fixed Drawers inside.
  const FullScreenPanel = ({ active = true, title, tabs, tab, onTab, headerExtra, onClose, onEscape,
    maxWidth = 1480, closeOnBackdrop = true, bodyScroll = true, ariaLabel, children }) => {
    const device = window.useDevice();
    const panelRef = React.useRef(null);
    const [panelEl, setPanelEl] = React.useState(null);
    const setPanel = React.useCallback((el) => { panelRef.current = el; setPanelEl(el); }, []);
    const [narrow, setNarrow] = React.useState(() => (window.innerWidth || 1280) < 1024);
    React.useEffect(() => {
      if (!active) return undefined;
      const onResize = () => setNarrow((window.innerWidth || 1280) < 1024);
      onResize();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, [active]);
    window.useFocusTrap(active, panelRef);
    // Layout phase: re-shown with a drawer still open inside it, the panel's
    // Escape entry lands below the drawer's (children register in passive effects).
    window.useEscapeKey(active, onEscape || onClose, { layout: true });
    // hidden: children drop their Escape/focus entries, phone sheets and menus.
    const layer = React.useMemo(() => ({ portalTarget: panelEl, zBase: core.Z.workspace, hidden: !active }), [panelEl, active]);
    const titleId = React.useId();
    const phone = !!device.phone;
    const full = phone || narrow;           // inset 0 on phones and under 1024px
    return ReactDOM.createPortal(
      <div data-atlas-workspace=""
        onClick={(e) => { if (closeOnBackdrop && e.target === e.currentTarget && onClose) onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: core.Z.workspace, display: active ? 'flex' : 'none',
          alignItems: 'stretch', justifyContent: 'center', padding: full ? 0 : 16,
          background: 'oklch(0.2 0.01 270 / 0.32)' }}>
        <div ref={setPanel} role="dialog" aria-modal="true"
          aria-labelledby={title ? titleId : undefined} aria-label={title ? undefined : ariaLabel}
          className={phone ? 'atlas-sheet' : undefined}
          style={{ width: '100%', maxWidth: full ? 'none' : maxWidth, minHeight: 0, display: 'flex', flexDirection: 'column',
            background: 'var(--surface)', borderRadius: full ? 0 : 12, border: full ? 'none' : '1px solid var(--border)',
            overflow: 'hidden', boxShadow: full ? 'none' : '0 24px 64px oklch(0.15 0.01 270 / 0.35)',
            paddingLeft: phone ? 'var(--sal)' : 0, paddingRight: phone ? 'var(--sar)' : 0 }}>
          <window.LayerContext.Provider value={layer}>
            <header style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10,
              padding: phone ? 'calc(var(--sat) + 6px) 8px 8px 14px' : '12px 14px 12px 18px',
              borderBottom: '1px solid var(--border)', flexWrap: phone ? 'wrap' : 'nowrap' }}>
              <h2 id={titleId} style={{ fontSize: phone ? 16 : 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.25,
                minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</h2>
              {tabs && tabs.length > 0 && (
                <div role="tablist" style={{ display: 'flex', gap: 4, minWidth: 0, overflowX: 'auto' }}>
                  {tabs.map((t) => (
                    <button key={t.key} type="button" role="tab" aria-selected={t.key === tab}
                      onClick={() => onTab && onTab(t.key)}
                      style={{ border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontSize: 12.5, fontWeight: t.key === tab ? 600 : 500,
                        background: t.key === tab ? 'var(--accent-soft)' : 'transparent',
                        color: t.key === tab ? 'var(--accent-text)' : 'var(--text-2)' }}>
                      {t.label}
                      {t.count != null && <span style={{ marginLeft: 5, fontSize: 11, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{t.count}</span>}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ flex: 1 }} />
              {headerExtra}
              <button type="button" onClick={onClose} aria-label="Close" title="Close (Esc)"
                style={{ width: phone ? 44 : 32, height: phone ? 44 : 32, borderRadius: 8, border: 'none', background: 'transparent',
                  color: 'var(--text-2)', fontSize: phone ? 26 : 22, lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}>×</button>
            </header>
            <div style={bodyScroll
              ? { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
              : { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
              {children}
            </div>
          </window.LayerContext.Provider>
          {phone && <div style={{ height: 'var(--sab, 0px)', flexShrink: 0 }} />}
        </div>
      </div>,
      document.body,
    );
  };
  window.FullScreenPanel = FullScreenPanel;

  // ── reference drawers: window.Drawer + a per-panel width key + a body portal
  // (or the workspace's panel element, when opened inside one).
  const RecordDrawer = ({ name, open, onClose, onEscape, title, subtitle, footer, width, widthKey,
    modeless = false, portal = true, top = 0, children }) => {
    const layer = React.useContext(window.LayerContext);
    const meta = core.panelMeta(name) || {};
    // Inside a workspace the panel element arrives one commit later (callback
    // ref); wait for it rather than portal to body and then re-portal (remount).
    if (portal && layer.zBase && !layer.portalTarget) return null;
    const drawer = (
      <window.Drawer open={open} onClose={onClose} onEscape={onEscape}
        title={title != null ? title : meta.title} subtitle={subtitle} footer={footer}
        width={width || meta.width || 480} widthKey={widthKey || name} modeless={modeless} top={top}>
        {children}
      </window.Drawer>
    );
    if (!portal) return drawer;
    return ReactDOM.createPortal(drawer, layer.portalTarget || document.body);
  };
  window.RecordDrawer = RecordDrawer;

  // ── the one gate + host for ?panel=, on every record page and surface (one
  // per page branch, right before NoteHost).
  const PanelHost = ({ kind, id, record, facts, variant, ctx, modeless = false, onPanelClose }) => {
    const rec = React.useMemo(() => ({ kind, id }), [kind, id]);
    const p = window.usePanelParam(rec);
    const guest = !!window.AtlasIsGuest;
    const surface = window.recordSurface();
    const verdict = recordPanelVerdict({ name: p.name, params: p.params, kind, surface, guest, facts });
    const current = verdict === 'ok' ? p.name : null;
    const hostOf = (n) => {
      const e = panelEntry(n);
      return e && e.host && e.host[surface] === 'host' && typeof e.render === 'function' ? e : null;
    };
    const curEntry = hostOf(current);
    const curWorkspace = curEntry && curEntry.form === 'workspace' ? current : null;
    const curDrawer = curEntry && curEntry.form === 'drawer' ? current : null;

    // Workspaces mount on first open and stay mounted (hidden) while the record
    // is open — the page is keyed per record, so this resets per record.
    const [mounted, setMounted] = React.useState(() => []);
    const mountedNow = curWorkspace && !mounted.includes(curWorkspace) ? [...mounted, curWorkspace] : mounted;
    React.useEffect(() => { if (mountedNow !== mounted) setMounted(mountedNow); }, [mountedNow, mounted]);
    const lastParams = React.useRef({});
    if (curWorkspace) lastParams.current[curWorkspace] = p.params;   // hidden ones keep their last params

    // One drawer slot; the last drawer (and its params) stays rendered closed so
    // the Drawer exit animation runs. Keyed by name below: another drawer is a
    // fresh Drawer (its own atlas.drawerW.<name> width, a fresh body).
    const [lastDrawer, setLastDrawer] = React.useState(null);
    React.useEffect(() => {
      if (curDrawer && (!lastDrawer || lastDrawer.name !== curDrawer || lastDrawer.params !== p.params)) {
        setLastDrawer({ name: curDrawer, params: p.params });
      }
    }, [curDrawer, p.params, lastDrawer]);
    const slot = curDrawer ? { name: curDrawer, params: p.params } : lastDrawer;

    // Forbidden / unknown / not applicable → strip before paint, no beacon.
    // close() = every PANEL_KEY → null through setRecordParams (back when the
    // entry is ours, else replace; the note stays). A pending Back makes it a
    // no-op, and the next hash change re-runs this.
    React.useLayoutEffect(() => {
      if (verdict !== 'strip') return;
      p.close({ via: 'link' });
    }, [verdict, p.name, p.params]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Beacons on appear / disappear (never for guests); onPanelClose on disappear.
    const shownRef = React.useRef(null);
    const variantRef = React.useRef(null);
    variantRef.current = variant || _VARIANT_FALLBACK[kind];
    React.useEffect(() => {
      const was = shownRef.current;
      if (was === current) return;
      shownRef.current = current;
      const reason = _takeReason(rec, was, current);
      const v = variantRef.current;
      if (was) _panelBeacon(kind, v, was, 'close', (reason && reason.via) || 'back');
      if (current) _panelBeacon(kind, v, current, 'open', (reason && reason.via) || 'link');
      if (was && onPanelClose) onPanelClose(was);
    }, [current]);   // eslint-disable-line react-hooks/exhaustive-deps
    // Leaving the record with a panel shown (another record or page, Back to
    // the list, a layout re-key) closes it too, via 'link', so opens and closes
    // pair up. Cleared, so a remount of the same page re-sends its open.
    React.useEffect(() => () => {
      const was = shownRef.current;
      shownRef.current = null;
      if (was) _panelBeacon(kind, variantRef.current, was, 'close', 'link');
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    if (!mountedNow.length && !slot) return null;
    const canWrite = window.recordCanWrite();
    // Escape on a hosted pop-out closes an open note first, whatever order the
    // Escape stack registered them in (a facts-gated entry can mount after NoteHost).
    const escape = () => (p.note ? p.closeNote({ via: 'escape' }) : p.close({ via: 'escape' }));
    const apiFor = (name, params, active) => ({
      active, name, params, set: p.set, close: p.close, escape, openNote: p.openNote,
      rec, record, facts: facts || EMPTY, ctx: ctx || EMPTY, canWrite, guest, surface,
    });
    const slotEntry = slot ? hostOf(slot.name) : null;
    const slotApi = slotEntry ? apiFor(slot.name, slot.params || EMPTY, slot.name === curDrawer) : null;
    // An entry's title is PANEL_META's string unless it overrides it with title(api).
    const slotTitle = slotEntry && (typeof slotEntry.title === 'function' ? slotEntry.title(slotApi) : slotEntry.title);
    return (
      <>
        {mountedNow.map((n) => {
          const e = hostOf(n);
          return e ? <React.Fragment key={n}>{e.render(apiFor(n, lastParams.current[n] || EMPTY, n === curWorkspace))}</React.Fragment> : null;
        })}
        {slotEntry && (
          <RecordDrawer key={slot.name} name={slot.name} open={slot.name === curDrawer}
            onClose={() => p.close({ via: 'button' })} onEscape={escape}
            title={slotTitle}
            width={slotEntry.width} widthKey={slot.name} modeless={modeless}>
            {slotEntry.render(slotApi)}
          </RecordDrawer>
        )}
      </>
    );
  };
  window.PanelHost = PanelHost;

  // Right-rail summary card: a Section in its 'rail' style (style only).
  const SummaryCard = (props) => <window.Section {...props} variant="rail" />;
  window.SummaryCard = SummaryCard;

  // ── later steps (4b overlays, 8 research) go above this line, inside the IIFE
})();
